// client/src/components/company/NewVatCreditRequest.js
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { sanitizeInput } from '../utils/sanitize';
import { parseCurrencyInput, handleCurrencyInputChange, getCurrencySymbol } from '../utils/formatUtils';
import { PlusCircle, Building2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { ACCEPT_ATTR, validateAttachmentFile } from '../utils/attachmentUtils';
import FileUploadField from '../utils/FileUploadField';
import { VAT_CREDIT_TYPES, VAT_CREDIT_COVERAGES, VAT_CREDIT_QUARTERS } from '../vatCredit/vatCreditUtils';

const CURRENCY_FIELDS = ['amount', 'refundRequestedAmount'];

// Anni selezionabili per il credito: anno corrente e i 9 precedenti
const CREDIT_YEAR_OPTIONS = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

const NewVatCreditRequest = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Crediti IVA è disponibile SOLO per le aziende paganti (Corporate).
  // A differenza di allocation/hot-money, il backend richiede isPaying === true in modo stretto
  // (non tratta undefined/legacy come pagante), quindi il gate qui deve essere altrettanto stretto.
  const isPaying = user?.organization?.isPaying === true;

  const [formData, setFormData] = useState({
    // Dati del credito
    creditType: '',
    creditYear: '',
    creditQuarter: '', // Solo per Modello TR
    refundRequestedAmount: '',
    maturityDate: '',
    coverage: '',
    // Gara e intermediari
    amount: '',
    executionDate: '',
    bidDeadline: '',
    bidDeadlineTime: '', // Campo orario opzionale (default 23:59 se vuoto)
    requestedRateTypes: ['fixed', 'floating'], // Entrambi pre-selezionati
    notes: '',
    selectedBanks: [] // Array di userIds
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [affiliatedBanks, setAffiliatedBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(true);

  // Allegati: dichiarazione IVA + deposito richiesta di rimborso (entrambi opzionali)
  const [attachmentFiles, setAttachmentFiles] = useState({
    vat_declaration: null,
    refund_request_deposit: null
  });

  const {
    creditType,
    creditYear,
    creditQuarter,
    refundRequestedAmount,
    maturityDate,
    coverage,
    amount,
    executionDate,
    bidDeadline,
    bidDeadlineTime,
    requestedRateTypes,
    notes,
    selectedBanks
  } = formData;

  // Helper function to get bank display name (must be before useMemo)
  const getBankDisplayName = (bank) => {
    if (bank.referralDetails?.bankName) {
      return bank.referralDetails.bankName;
    }
    if (bank.bankName) {
      return bank.bankName;
    }
    return `${bank.firstName || ''} ${bank.lastName || ''}`.trim();
  };

  // Per i Crediti IVA sono selezionabili solo le banche di relazione, non le banche partner
  const groupedBanks = useMemo(() => {
    const groups = {};

    affiliatedBanks.forEach(bank => {
      const bankName = getBankDisplayName(bank);

      if (!groups[bankName]) {
        groups[bankName] = {
          bankName,
          users: []
        };
      }

      groups[bankName].users.push({
        userId: bank._id || bank.userId,
        firstName: bank.firstName,
        lastName: bank.lastName,
        email: bank.email
      });
    });

    return Object.values(groups)
      .map(group => ({
        ...group,
        users: group.users.filter(u => !!u.userId)
      }))
      .filter(group => group.users.length > 0);
  }, [affiliatedBanks]);

  useEffect(() => {
    const fetchBanks = async () => {
      try {
        setLoadingBanks(true);

        const response = await axios.get('/api/company/bank-referrals');
        setAffiliatedBanks(response.data.bankReferrals || []);
      } catch (err) {
        console.error('Error fetching banks:', err);
        setAffiliatedBanks([]);
      } finally {
        setLoadingBanks(false);
      }
    };

    fetchBanks();
  }, []);

  const onChange = e => {
    const { name, value } = e.target;
    if (CURRENCY_FIELDS.includes(name)) {
      handleCurrencyInputChange(e, setFormData, formData, name);
    } else if (name === 'creditType') {
      // Il trimestre esiste solo per il Modello TR
      setFormData({
        ...formData,
        creditType: value,
        creditQuarter: value === 'tr_model' ? formData.creditQuarter : ''
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const onRateTypeToggle = (rateType) => {
    const updated = requestedRateTypes.includes(rateType)
      ? requestedRateTypes.filter(rt => rt !== rateType)
      : [...requestedRateTypes, rateType];
    setFormData({ ...formData, requestedRateTypes: updated });
  };

  const onAttachmentSelect = (docType) => (file) => {
    if (!file) {
      setAttachmentFiles(prev => ({ ...prev, [docType]: null }));
      return;
    }

    const errKey = validateAttachmentFile(file);
    if (errKey) {
      setError(t(errKey));
      return;
    }

    setError('');
    setAttachmentFiles(prev => ({ ...prev, [docType]: file }));
  };

  const removeAttachment = (docType) => {
    setAttachmentFiles(prev => ({ ...prev, [docType]: null }));
  };

  // Toggle per gruppo di banca (tutti gli utenti insieme)
  const toggleBankGroupSelection = (bankGroup) => {
    const userIds = bankGroup.users.map(u => u.userId);

    const allSelected = userIds.every(id => selectedBanks.includes(id));

    let updatedBanks;
    if (allSelected) {
      updatedBanks = selectedBanks.filter(id => !userIds.includes(id));
    } else {
      updatedBanks = [...new Set([...selectedBanks, ...userIds])];
    }

    setFormData({ ...formData, selectedBanks: updatedBanks });
  };

  const isBankGroupSelected = (bankGroup) => {
    const userIds = bankGroup.users.map(u => u.userId);
    return userIds.every(id => selectedBanks.includes(id));
  };

  const isAllBanksSelected = groupedBanks.length > 0 && groupedBanks.every(bg =>
    bg.users.every(u => selectedBanks.includes(u.userId))
  );

  const toggleAllBanks = () => {
    const allUserIds = groupedBanks.flatMap(bg => bg.users.map(u => u.userId));
    const updatedBanks = isAllBanksSelected
      ? selectedBanks.filter(id => !allUserIds.includes(id))
      : [...new Set([...selectedBanks, ...allUserIds])];

    setFormData({ ...formData, selectedBanks: updatedBanks });
  };

  const formatDate = (daysToAdd = 0) => {
    const date = new Date();
    if (daysToAdd) {
      date.setDate(date.getDate() + daysToAdd);
    }
    const year = date.getFullYear();
    let month = date.getMonth() + 1;
    let day = date.getDate();

    month = month < 10 ? `0${month}` : month;
    day = day < 10 ? `0${day}` : day;

    return `${year}-${month}-${day}`;
  };

  const onSubmit = async e => {
    e.preventDefault();
    setError('');

    const parsedRefundRequested = parseCurrencyInput(refundRequestedAmount);
    const formattedAmount = parseCurrencyInput(amount);

    // Restituisce la chiave di traduzione del primo errore, o null se il form è valido
    const getValidationError = () => {
      // --- Dati del credito ---
      if (!creditType) return 'vatCredit.validation.creditTypeRequired';
      if (!creditYear) return 'vatCredit.validation.creditYearRequired';
      if (creditType === 'tr_model' && !creditQuarter) return 'vatCredit.validation.creditQuarterRequired';
      if (isNaN(parsedRefundRequested) || parsedRefundRequested <= 0) return 'vatCredit.validation.validRefundRequestedAmount';
      if (!maturityDate) return 'vatCredit.validation.maturityDateRequired';
      if (!coverage) return 'vatCredit.validation.coverageRequired';

      // --- Gara e intermediari ---
      if (isNaN(formattedAmount) || formattedAmount <= 0) return 'forms.validation.validAmount';
      if (requestedRateTypes.length === 0) return 'vatCredit.validation.requestedRateTypeRequired';
      if (!executionDate) return 'vatCredit.validation.executionDateRequired';
      if (!bidDeadline) return 'forms.validation.deadlineRequired';
      if (new Date(executionDate) < new Date(bidDeadline)) return 'vatCredit.validation.executionAfterDeadline';
      if (new Date(maturityDate) <= new Date(executionDate)) return 'vatCredit.validation.maturityAfterExecution';
      if (selectedBanks.length === 0) return 'vatCredit.validation.selectAtLeastOneBank';
      return null;
    };

    const validationError = getValidationError();
    if (validationError) {
      setError(t(validationError));
      return;
    }

    setLoading(true);

    try {
      const requestData = {
        creditType,
        creditYear: parseInt(creditYear, 10),
        creditQuarter: creditType === 'tr_model' ? parseInt(creditQuarter, 10) : undefined,
        refundRequestedAmount: parsedRefundRequested,
        maturityDate,
        coverage,
        amount: formattedAmount,
        executionDate,
        bidDeadline,
        bidDeadlineTime: bidDeadlineTime || '',
        requestedRateTypes,
        selectedBanks,
        notes: sanitizeInput(notes)
      };

      const res = await axios.post('/api/vat-credit-requests', requestData);
      const newRequestId = res.data?.request?.id;

      // Upload allegati (se presenti) subito dopo la creazione della richiesta
      const hasAttachments = attachmentFiles.vat_declaration || attachmentFiles.refund_request_deposit;
      if (newRequestId && hasAttachments) {
        const fd = new FormData();
        if (attachmentFiles.vat_declaration) {
          fd.append('vat_declaration', attachmentFiles.vat_declaration);
        }
        if (attachmentFiles.refund_request_deposit) {
          fd.append('refund_request_deposit', attachmentFiles.refund_request_deposit);
        }

        try {
          await axios.post(`/api/vat-credit-requests/${newRequestId}/attachments`, fd);
        } catch (uploadErr) {
          // La richiesta è stata creata: porta l'utente al dettaglio per riprovare l'upload
          setError(t('vatCredit.attachments.uploadError'));
          setLoading(false);
          navigate(`/company/vat-credit/requests/${newRequestId}`);
          return;
        }
      }

      navigate('/company/vat-credit/past-requests');
    } catch (err) {
      setError(err.response?.data?.message || t('common.errors.createRequest', {
        defaultValue: 'Failed to create request'
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="company-dashboard">
      <div className="card">
        <div className="card-title">
          <PlusCircle className="card-title-icon" size={20} />
          <span>{t('vatCredit.newRequest')}</span>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {!isPaying && (
          <div className="alert alert-danger">{t('vatCredit.notAvailableForPlan')}</div>
        )}

        <form id="new-vat-credit-request-form" className="request-form" onSubmit={onSubmit}>
          {/* --- Dati del credito --- */}
          <div className="form-section-head">
            <span className="t">{t('vatCredit.sectionCreditData')}</span>
            <span className="rule" />
          </div>

          <div className={`form-row ${creditType === 'tr_model' ? 'three' : 'two'}`}>
            <div className="form-group">
              <label htmlFor="creditType">
                {t('vatCredit.creditType')} <span className="req">*</span>
              </label>
              <select
                id="creditType"
                name="creditType"
                className="form-select-custom"
                value={creditType}
                onChange={onChange}
                required
              >
                <option value="">{t('vatCredit.selectCreditType')}</option>
                {VAT_CREDIT_TYPES.map(type => (
                  <option key={type} value={type}>{t(`vatCredit.creditTypes.${type}`)}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="creditYear">
                {t('vatCredit.creditYear')} <span className="req">*</span>
              </label>
              <select
                id="creditYear"
                name="creditYear"
                className="form-select-custom"
                value={creditYear}
                onChange={onChange}
                required
              >
                <option value="">{t('vatCredit.selectCreditYear')}</option>
                {CREDIT_YEAR_OPTIONS.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            {creditType === 'tr_model' && (
              <div className="form-group">
                <label htmlFor="creditQuarter">
                  {t('vatCredit.creditQuarter')} <span className="req">*</span>
                </label>
                <select
                  id="creditQuarter"
                  name="creditQuarter"
                  className="form-select-custom"
                  value={creditQuarter}
                  onChange={onChange}
                  required
                >
                  <option value="">{t('vatCredit.selectCreditQuarter')}</option>
                  {VAT_CREDIT_QUARTERS.map(q => (
                    <option key={q} value={q}>{t('vatCredit.quarterOption', { n: q })}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="form-row two">
            <div className="form-group">
              <label htmlFor="refundRequestedAmount">
                {t('vatCredit.refundRequestedAmount')} <span className="req">*</span>
              </label>
              <div className="amount-field">
                <span className="sym">{getCurrencySymbol('EUR')}</span>
                <input
                  type="text"
                  id="refundRequestedAmount"
                  name="refundRequestedAmount"
                  placeholder={t('forms.enterAmount')}
                  value={refundRequestedAmount}
                  onChange={onChange}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="amount">
                {t('vatCredit.amountToAssign')} <span className="req">*</span>
              </label>
              <div className="amount-field">
                <span className="sym">{getCurrencySymbol('EUR')}</span>
                <input
                  type="text"
                  id="amount"
                  name="amount"
                  placeholder={t('forms.enterAmount')}
                  value={amount}
                  onChange={onChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-row two">
            <div className="form-group">
              <label htmlFor="maturityDate">
                {t('vatCredit.maturityDate')} <span className="req">*</span>
              </label>
              <input
                type="date"
                id="maturityDate"
                name="maturityDate"
                value={maturityDate}
                onChange={onChange}
                min={executionDate || formatDate(1)}
                required
              />
              <div className="field-help dark">
                <span>{t('vatCredit.maturityDateHelp')}</span>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="coverage">
                {t('vatCredit.coverage')} <span className="req">*</span>
              </label>
              <select
                id="coverage"
                name="coverage"
                className="form-select-custom"
                value={coverage}
                onChange={onChange}
                required
              >
                <option value="">{t('vatCredit.selectCoverage')}</option>
                {VAT_CREDIT_COVERAGES.map(c => (
                  <option key={c} value={c}>{t(`vatCredit.coverages.${c}`)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* --- Allegati --- */}
          <div className="form-section-head">
            <span className="t">{t('vatCredit.attachments.sectionTitle')}</span>
            <span className="rule" />
          </div>

          <div className="field-help dark" style={{ marginBottom: '12px' }}>
            <span>{t('vatCredit.attachments.visibilityHelp')}</span>
          </div>

          {[
            { docType: 'vat_declaration', label: t('vatCredit.attachments.vatDeclaration') },
            { docType: 'refund_request_deposit', label: t('vatCredit.attachments.refundRequestDeposit') }
          ].map(({ docType, label }) => {
            const file = attachmentFiles[docType];
            return (
              <div className="form-group" key={docType}>
                <label htmlFor={`attachment-${docType}`}>
                  {label} <span style={{ color: '#6c757d' }}>{t('vatCredit.attachments.optional')}</span>
                </label>

                <FileUploadField
                  id={`attachment-${docType}`}
                  accept={ACCEPT_ATTR}
                  file={file}
                  onSelect={onAttachmentSelect(docType)}
                  onClear={() => removeAttachment(docType)}
                  ctaText={t('vatCredit.attachments.dropzoneCta')}
                  hintText={t('vatCredit.attachments.uploadHelp')}
                  changeText={t('vatCredit.attachments.change')}
                  removeText={t('vatCredit.attachments.remove')}
                />
              </div>
            );
          })}

          {/* --- Gara e intermediari --- */}
          <div className="form-section-head">
            <span className="t">{t('vatCredit.sectionAuction')}</span>
            <span className="rule" />
          </div>

          <div className="form-group">
            <label>
              {t('vatCredit.requestedRateType')} <span className="req">*</span>
            </label>
            <div className="chip-group">
              {['fixed', 'floating'].map(rateType => (
                <label
                  key={rateType}
                  className={"chip-check" + (requestedRateTypes.includes(rateType) ? " on" : "")}
                >
                  <span className="box">✓</span>
                  <span>{t(`vatCredit.rateTypes.${rateType}`)}</span>
                  <input
                    type="checkbox"
                    id={`requestedRateType-${rateType}`}
                    checked={requestedRateTypes.includes(rateType)}
                    onChange={() => onRateTypeToggle(rateType)}
                  />
                </label>
              ))}
            </div>
            <div className="field-help dark">
              <span>{t('vatCredit.requestedRateTypeHelp')}</span>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="executionDate">
              {t('vatCredit.executionDate')} <span className="req">*</span>
            </label>
            <input
              type="date"
              id="executionDate"
              name="executionDate"
              value={executionDate}
              onChange={onChange}
              min={bidDeadline || formatDate()}
              required
            />
            <div className="field-help dark">
              <span>{t('vatCredit.executionDateHelp')}</span>
            </div>
          </div>

          <div className="form-row deadline">
            <div className="form-group">
              <label htmlFor="bidDeadline">
                {t('vatCredit.bidDeadline')} <span className="req">*</span>
              </label>
              <input
                type="date"
                id="bidDeadline"
                name="bidDeadline"
                value={bidDeadline}
                onChange={onChange}
                min={formatDate()}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="bidDeadlineTime">
                {t('requests.bidTimeLabel')}
              </label>
              <input
                type="time"
                id="bidDeadlineTime"
                name="bidDeadlineTime"
                value={bidDeadlineTime}
                onChange={onChange}
                placeholder="23:59"
              />
              <div className="field-help sm">
                <span>{t('requests.deadlineTimeHelp', { defaultValue: 'Default: 23:59 if not specified' })}</span>
              </div>
            </div>
          </div>

          {!loadingBanks && (
            <>
              <div className="form-section-head">
                <span className="t">{t('vatCredit.invitedBanks')}</span>
                <span className="rule" />
                <span className="count">
                  {groupedBanks.filter(bg => bg.users.some(u => selectedBanks.includes(u.userId))).length} {t('requests.banksSelected') || 'bank(s) selected'}
                </span>
              </div>

              {groupedBanks.length > 0 ? (
                <div className="bank-panel">
                  <div className="bank-panel-head">
                    <Building2 size={14} color="#35507d" />
                    <span className="title">{t('requests.relationshipBanks') || 'Relationship Banks'}</span>
                    <button
                      type="button"
                      className="select-all"
                      onClick={toggleAllBanks}
                    >
                      {isAllBanksSelected ? t('requests.deselectAllBanks') : t('requests.selectAllBanks')}
                    </button>
                  </div>
                  {groupedBanks.map((bankGroup, index) => (
                    <div
                      key={`vc-bank-${index}`}
                      className={"bank-row" + (isBankGroupSelected(bankGroup) ? " on" : "")}
                      onClick={() => toggleBankGroupSelection(bankGroup)}
                    >
                      <span className="box">✓</span>
                      <span className="name">{bankGroup.bankName}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="form-group">
                  <small style={{ color: '#6c757d' }}>{t('vatCredit.noBanksAvailable')}</small>
                </div>
              )}
            </>
          )}

          {loadingBanks && (
            <div className="form-group">
              <small style={{ color: '#6c757d' }}>
                {t('common.loading') || 'Loading banks...'}
              </small>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="notes">
              {t('requests.additionalNotes')}
            </label>
            <textarea
              id="notes"
              name="notes"
              rows="4"
              placeholder={t('forms.additionalRequirements')}
              value={notes}
              onChange={onChange}
            ></textarea>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-light"
              onClick={() => navigate('/company/dashboard')}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !isPaying}
            >
              {loading ? t('actions.submitting') : t('common.submitRequest')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewVatCreditRequest;
